import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  IconButton,
  Input,
} from "@material-tailwind/react";
import { FiSearch } from "react-icons/fi";
import { RiHome4Line, RiMedicineBottleLine, RiLeafLine, RiBuilding2Line, RiFlaskLine } from "react-icons/ri";
import { HiXMark } from "react-icons/hi2";
import { HiMenu, HiOutlineDocumentText, HiOutlineShieldCheck, HiOutlineInformationCircle, HiOutlineNewspaper } from "react-icons/hi";

const LINKS = [
  {
    title: "Home",
    href: "/",
    icon: RiHome4Line,
  },
  {
    title: "Trade names",
    href: "/tradenames",
    icon: RiMedicineBottleLine,
  },
  {
    title: "Ingredients",
    href: "/ingredients",
    icon: RiLeafLine,
  },
  {
    title: "Combine Ingredients",
    href: "/ingredients/search",
    icon: RiFlaskLine,
  },
  {
    title: "Manufacturers",
    href: "/manufacturers",
    icon: RiBuilding2Line,
  },
  {
    title: "Blog",
    href: "/blog",
    icon: HiOutlineNewspaper,
  },
  {
    title: "Terms & Conditions",
    href: "/terms",
    icon: HiOutlineDocumentText,
  },
  {
    title: "Privacy Policy",
    href: "/policy",
    icon: HiOutlineShieldCheck,
  },
  {
    title: "About Us",
    href: "/about",
    icon: HiOutlineInformationCircle,
  },
  // {
  //   title: "Reviews",
  //   href: "/",
  // },
];

const PRIMARY_LINKS = LINKS.slice(0, 6);
const SECONDARY_LINKS = LINKS.slice(6);

function NavList() {
  const location = useLocation();

  const isActive = (path) => {
    // Special case for home path
    if (path === '/') {
      return location.pathname === '/';
    }
    
    // Handle both singular and plural forms for ingredients, manufacturers, and tradenames
    if (path === '/ingredients' && (location.pathname.startsWith('/ingredients/') || location.pathname.startsWith('/ingredients/'))) {
      return true;
    }
    if (path === '/manufacturers' && (location.pathname.startsWith('/manufacturers/') || location.pathname.startsWith('/manufacturers/'))) {
      return true;
    }
    if (path === '/tradenames' && (location.pathname.startsWith('/tradenames/') || location.pathname.startsWith('/tradenames/'))) {
      return true;
    }
    if (path === '/blog' && location.pathname.startsWith('/blog')) {
      return true;
    }
    
    // Check if current path starts with the given path
    return location.pathname.startsWith(path);
  };

  return (
    <ul className="flex flex-col m-2 gap-x-6 gap-y-1 lg:m-0 lg:flex-row lg:items-center">
      {LINKS.map(({ title, href }) => (
        <li key={title}>
          <Link
            to={href}
            className={`p-1 text-12 font-text hover:text-primary text-secondary/70 ${isActive(href)
                ? "!text-primary font-medium"
                : ""
              }`}
          >
            {title}
          </Link>
        </li>
      ))}
    </ul>
  );
}

function MobileNavList() {
  const location = useLocation();

  const isActive = (path) => {
    // Special case for home path
    if (path === '/') {
      return location.pathname === '/';
    }
    
    // Handle both singular and plural forms for ingredients, manufacturers, and tradenames
    if (path === '/ingredients' && (location.pathname.startsWith('/ingredients/') || location.pathname.startsWith('/ingredients/'))) {
      return true;
    }
    if (path === '/manufacturers' && (location.pathname.startsWith('/manufacturers/') || location.pathname.startsWith('/manufacturers/'))) {
      return true;
    }
    if (path === '/tradenames' && (location.pathname.startsWith('/tradenames/') || location.pathname.startsWith('/tradenames/'))) {
      return true;
    }
    if (path === '/blog' && location.pathname.startsWith('/blog')) {
      return true;
    }
    
    // Check if current path starts with the given path
    return location.pathname.startsWith(path);
  };

  return (
    <div className="px-4 py-3 border-t border-light-gray/10 bg-white/90">
      {/* Primary links in a responsive grid */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-x-2 gap-y-2">
        {PRIMARY_LINKS.map(({ title, href, icon: Icon }) => (
          <Link
            key={title}
            to={href}
            className={`flex items-center px-3 py-2 text-center rounded-lg bg-primary/5 ${isActive(href)
                ? "!bg-primary"
                : ""
              }`}
          >
            <Icon className={`mr-2 size-4 ${isActive(href) ? 'text-white' : 'text-primary/60'}`} />
            <span className={`text-14 ${isActive(href) ? 'text-white' : 'text-secondary/80'}`}>{title}</span>
          </Link>
        ))}
      </div>

      {/* Secondary links in a cleaner layout without icons */}
      <div className="pt-3 mt-3 border-t border-light-gray/10">
        <div className="flex flex-wrap justify-center sm:justify-between gap-x-5 gap-y-2">
          {SECONDARY_LINKS.map(({ title, href, icon: Icon }) => (
            <Link
              key={title}
              to={href}
              className={`flex items-center transition-colors text-12 text-light-gray/70 hover:text-primary ${isActive(href)
                  ? "text-primary font-medium"
                  : ""
                }`}
            >
              {Icon && <Icon className="mr-1 size-3 opacity-70" />}
              {title}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

const Header = () => {
  const [openNav, setOpenNav] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    window.addEventListener(
      "resize",
      () => window.innerWidth >= 960 && setOpenNav(false),
    );
  }, []);

  useEffect(() => {
    if (showSearch) {
      const timer = setTimeout(() => {
        const inputElement = document.querySelector('.search-input input');
        if (inputElement) {
          inputElement.focus();
        }
      }, 200);

      return () => clearTimeout(timer);
    }
  }, [showSearch]);

  const toggleSearch = () => {
    setShowSearch(!showSearch);
  };

  const handleSearchBlur = () => {
    if (!searchValue.trim()) {
      setShowSearch(false);
    }
  };

  const handleSearchChange = (e) => {
    setSearchValue(e.target.value);
  };

  const handleSearchClear = () => {
    setSearchValue('');
    setShowSearch(false);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSearchClear();
      navigate(`/search?q=${encodeURIComponent(e.target.value)}&type=all&page=1`);
    }
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-white/80 backdrop-blur-md border-white/20">
      <div className="container flex items-center justify-between h-[44px] max-md:pl-4 max-md:pr-2 mx-auto">
        <Link to="/" className="flex items-center">
          <img src='/logo.svg' alt="logo" className="size-5" />
          <div className="ml-2 font-bold text-black text-14 opacity-80">
            Pharmacy <span className="opacity-50 text-12">wiki</span>
          </div>
        </Link>
        <div className="hidden lg:ml-auto lg:mr-2 lg:block">
          <NavList />
        </div>
        <div className="flex items-center">
          <div className={`transition-all duration-200 ease-in-out flex justify-end ${showSearch ? 'w-[160px]' : 'w-[40px]'}`}>
            {showSearch ? (
              <Input
                placeholder="Search..."
                className="w-full h-8 text-black rounded-full shadow-none text-14 bg-secondary bg-opacity-5 search-input"
                onBlur={handleSearchBlur}
                onChange={handleSearchChange}
                value={searchValue}
                onKeyDown={handleSearchKeyDown}
                autoFocus
              >
                <Input.Icon
                  placement="end"
                  className="flex items-center justify-end cursor-pointer"
                  onClick={searchValue ? handleSearchClear : null}
                >
                  <FiSearch className="size-4" />
                </Input.Icon>
              </Input>
            ) : (
              <IconButton
                size="sm"
                variant="ghost"
                color="secondary"
                onClick={toggleSearch}
              >
                <FiSearch className="size-4" />
              </IconButton>
            )}
          </div>
          <IconButton
            size="sm"
            variant="ghost"
            color="secondary"
            onClick={() => setOpenNav(!openNav)}
            className="grid ml-auto lg:hidden"
          >
            {openNav ? (
              <HiXMark className="size-4" />
            ) : (
              <HiMenu className="size-4" />
            )}
          </IconButton>
        </div>
      </div>
      <div className={`relative z-50 transition-all duration-300 ${openNav ? 'translate-y-0 opacity-100' : '-translate-y-4 opacity-0 pointer-events-none'}`}>
        {openNav && <MobileNavList />}
      </div>
    </header>
  );
};

export default Header;
